import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calculator } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { PatientTable, newPatient, rowTotal, sumMode } from "@/components/PatientTable";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const shiftDays = (iso, days) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const EMPTY = (pid) => ({ date: today(), autre: "", logiciel: "", practitioner_id: pid || "", site_id: "", cb_directe: false, patients: [newPatient()] });
const s = (v) => (v ? String(v) : "");

const rowsFromEntry = (e) => {
  if (e.patients?.length) return e.patients.map((p) => ({ ...p, cb: s(p.cb), especes: s(p.especes), cheque: s(p.cheque), secu: s(p.secu), mutuelle: s(p.mutuelle), alma: s(p.alma) }));
  const rows = [];
  if (e.cb || e.especes || e.cheque) rows.push({ ...newPatient(), cb: s(e.cb), especes: s(e.especes), cheque: s(e.cheque) });
  (e.secu_lines || []).forEach((l) => rows.push({ ...newPatient(), patient: l.patient, secu: s(l.amount) }));
  (e.mutuelle_lines || []).forEach((l) => rows.push({ ...newPatient(), patient: l.patient, mutuelle: s(l.amount), mutuelle_name: l.mutuelle || "" }));
  if (!(e.secu_lines || []).length && e.secu) rows.push({ ...newPatient(), secu: s(e.secu) });
  if (!(e.mutuelle_lines || []).length && e.mutuelle) rows.push({ ...newPatient(), mutuelle: s(e.mutuelle) });
  return rows.length ? rows : [newPatient()];
};

export default function DailyRevenueDialog({ open, onOpenChange, onSaved, practitionerId, editing }) {
  const [f, setF] = useState(() => EMPTY(practitionerId));
  const [prats, setPrats] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cb = sumMode(f.patients, "cb");
  const secu = sumMode(f.patients, "secu");
  const alma = sumMode(f.patients, "alma");
  const mutuelle = sumMode(f.patients, "mutuelle");
  const total = f.patients.reduce((t, p) => t + rowTotal(p), 0) + (parseFloat(f.autre) || 0);
  const ecart = total - (parseFloat(f.logiciel) || 0);

  useEffect(() => {
    if (!open) return;
    api.get("/practitioners").then((r) => setPrats(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    if (editing) {
      setF({ date: editing.date, autre: s(editing.autre), logiciel: s(editing.logiciel), practitioner_id: editing.practitioner_id || "", site_id: editing.site_id || "",
        cb_directe: editing.cb_directe ?? false, patients: rowsFromEntry(editing) });
    } else {
      setF((p) => ({ ...p, practitioner_id: practitionerId || p.practitioner_id || "" }));
    }
  }, [open, practitionerId, editing]);

  const pratSites = [...new Set((prats.find((p) => p.id === f.practitioner_id)?.locations || []).map((l) => l.site_id).filter(Boolean))];
  const siteOpts = [...sites.filter((x) => pratSites.includes(x.id)), ...sites.filter((x) => !pratSites.includes(x.id))];
  const autoSite = sites.length === 1 ? sites[0].id : pratSites.length === 1 ? pratSites[0] : "";
  useEffect(() => {
    if (!f.site_id && autoSite) setF((p) => ({ ...p, site_id: autoSite }));
  }, [f.site_id, autoSite]);

  const submit = async () => {
    if (!f.practitioner_id || f.practitioner_id === "none") { toast.error("Le praticien est obligatoire"); return; }
    if (sites.length > 1 && !f.site_id) { toast.error("Choisissez le site d'encaissement"); return; }
    const rows = f.patients.filter((p) => p.patient.trim() || rowTotal(p) > 0);
    const bad = rows.find((p) => !p.patient.trim() || !(rowTotal(p) > 0));
    if (bad) { toast.error(bad.patient.trim() ? `Montant manquant pour ${bad.patient}` : "Le nom du patient est obligatoire sur chaque ligne"); return; }
    setLoading(true);
    try {
      const payload = { ...f, patients: rows.map((p) => ({ ...p, id: String(p.id).startsWith("tmp-") ? undefined : p.id })) };
      if (editing) {
        await api.put(`/daily-revenues/${editing.id}`, payload);
        toast.success("Saisie modifiée");
      } else {
        await api.post("/daily-revenues", payload);
        toast.success(`Total encaissé : ${fmtEUR2(total)}`);
      }
      onOpenChange(false);
      setF(EMPTY(practitionerId));
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-4xl" data-testid="daily-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{editing ? "Modifier la saisie" : "Chiffre d'affaires du jour"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3 sm:max-w-2xl sm:grid-cols-3">
            <div>
              <Label>Date</Label>
              <Input data-testid="daily-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>Praticien <span className="text-rose-500">*</span></Label>
              <Select value={f.practitioner_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, practitioner_id: v, site_id: "" }))}>
                <SelectTrigger data-testid="daily-practitioner" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir un praticien" /></SelectTrigger>
                <SelectContent>
                  {prats.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {sites.length > 1 && (
              <div className="col-span-2 sm:col-span-1">
                <Label>Site d'encaissement <span className="text-rose-500">*</span></Label>
                <Select value={f.site_id || undefined} onValueChange={(v) => set("site_id", v)}>
                  <SelectTrigger data-testid="daily-site" className="mt-1.5 rounded-xl"><SelectValue placeholder="Choisir le site" /></SelectTrigger>
                  <SelectContent>
                    {siteOpts.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <PatientTable rows={f.patients} onChange={(v) => set("patients", v)} />
          <div className="sm:max-w-xs">
            <Label>Autre / non identifié</Label>
            <Input data-testid="daily-autre" type="number" step="0.01" value={f.autre} onChange={(e) => set("autre", e.target.value)} className="mt-1.5 rounded-xl" placeholder="0" />
          </div>
          {cb > 0 && (
            <label className="flex cursor-pointer items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="daily-cb-directe-label">
              <input type="checkbox" data-testid="daily-cb-directe" checked={!!f.cb_directe} onChange={(e) => set("cb_directe", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#065F46] accent-[#065F46]" />
              <span>CB encaissée <b>directement par le praticien</b> (TPE perso) — ce montant ({fmtEUR2(cb)}) sera déduit de sa rétrocession.</span>
            </label>
          )}
          {(secu > 0 || mutuelle > 0 || alma > 0) && (
            <div className="rounded-2xl bg-blue-50 p-3 text-xs text-blue-900" data-testid="deferred-receipts-note">
              <p className="mb-1 font-semibold uppercase tracking-wide text-blue-700">Encaissements différés</p>
              {secu > 0 && <p>Sécurité sociale / CMU : <b>{fmtEUR2(secu)}</b> — virement prévu le <b>{fmtDate(shiftDays(f.date, 7))}</b></p>}
              {alma > 0 && <p>Alma : <b>{fmtEUR2(alma)}</b> — virement prévu le <b>{fmtDate(shiftDays(f.date, 3))}</b></p>}
              {mutuelle > 0 && <p>Mutuelles : <b>{fmtEUR2(mutuelle)}</b> — virement prévu le <b>{fmtDate(shiftDays(f.date, 14))}</b></p>}
              <p className="mt-1 text-[11px] text-blue-600">Un virement attendu est créé par patient, à pointer individuellement.</p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-emerald-50 p-4 text-center">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Total encaissé du jour</p>
              <p data-testid="daily-total" className="font-display text-3xl font-black text-emerald-800">{fmtEUR2(total)}</p>
            </div>
            <div>
              <Label className="flex items-center gap-1"><Calculator className="h-3.5 w-3.5" /> CA logiciel / Logos (optionnel)</Label>
              <Input data-testid="daily-logiciel" type="number" step="0.01" value={f.logiciel} onChange={(e) => set("logiciel", e.target.value)} className="mt-1.5 rounded-xl" placeholder="Montant enregistré en logiciel" />
              {parseFloat(f.logiciel) > 0 && (
                <p className={`mt-2 text-sm font-semibold ${Math.abs(ecart) < 0.01 ? "text-emerald-600" : "text-amber-600"}`}>Écart logiciel / encaissé : {fmtEUR2(ecart)}</p>
              )}
            </div>
          </div>
          <Button data-testid="daily-submit" onClick={submit} disabled={loading} className="w-full rounded-full bg-[#065F46] py-6 font-semibold hover:bg-[#047857]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Enregistrer les modifications" : "Enregistrer la journée"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
